import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Star, Users, ArrowRight, ChevronRight } from 'lucide-react';
import { customerUnitPrice } from '../utils/planningConstants';
import SEO from '../components/SEO';
import { collectionPageLD, organizationLD, breadcrumbLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = [
  { key: 'all', label: 'All Cities' },
  { key: 'denver', label: 'Denver, CO', slug: 'denver', tagline: 'Mile High Events' },
  { key: 'las_vegas', label: 'Las Vegas, NV', slug: 'las-vegas', tagline: 'Entertainment Capital' },
  { key: 'los_angeles', label: 'Los Angeles, CA', slug: 'los-angeles', tagline: 'City of Stars' },
  { key: 'charlotte', label: 'Charlotte, NC', slug: 'charlotte', tagline: 'Queen City Events' },
];

const CATEGORIES = [
  { key: 'all', label: 'All Services' },
  { key: 'food', label: 'Food & Catering' },
  { key: 'drinks', label: 'Bar & Drinks' },
  { key: 'entertainment', label: 'Entertainment' },
  { key: 'staffing', label: 'Staffing' },
  { key: 'logistics', label: 'Logistics' },
  { key: 'photography', label: 'Photography' },
  { key: 'decor', label: 'Decor' },
];

export default function ServicesShowcase() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/services/products?limit=200`);
      setServices(res.data?.products || []);
    } catch {} finally { setLoading(false); }
  };

  const filtered = services.filter(s => {
    if (selectedCity !== 'all' && s.city !== selectedCity) return false;
    if (selectedCategory !== 'all' && s.category !== selectedCategory) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !(s.description || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const cityCount = (city) => services.filter(s => s.city === city).length;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Eventism",
    "description": "Premium event services platform — catering, DJs, photo booths, and full event planning across Denver, Las Vegas, Los Angeles, and Charlotte.",
    "url": "https://eventism.com/services",
    "areaServed": ["Denver", "Las Vegas", "Los Angeles", "Charlotte"],
    "serviceType": "Event Planning",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Event Services",
      "itemListElement": filtered.slice(0, 10).map(s => ({
        "@type": "Offer",
        "itemOffered": { "@type": "Service", "name": s.name, "description": s.short_description || s.description },
        "price": customerUnitPrice(s),
        "priceCurrency": "USD"
      }))
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Event Services — Catering, DJs, Photo Booths, Bartenders & More"
        description="Browse 90+ premium event services across Denver, Las Vegas, Los Angeles & Charlotte. Catering, DJs, photo booths, bartenders, decor & more. Get an instant quote."
        keywords={[
          'event services',
          'event catering',
          'wedding catering',
          'DJ for hire',
          'event photographer',
          'photo booth rental',
          'event bartender',
          'event decor',
          'event staffing',
          'event rentals',
          'Denver event services',
          'Las Vegas event services',
          'Los Angeles event services',
          'Charlotte event services',
          'book event vendors',
          'event planning services',
        ]}
        structuredData={[structuredData, organizationLD(), collectionPageLD('Event Services', 'Premium event services across 4 cities.'), breadcrumbLD([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }])]}
      />

      {/* Hero */}
      <section className="pt-24 pb-12 px-4 text-center bg-gradient-to-b from-indigo-500/10 to-transparent">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">
            Event Services That <span className="text-indigo-500">Deliver</span>
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Browse our curated catalog of premium event services across Denver, Las Vegas, Los Angeles & Charlotte. Every vendor vetted. Every detail covered.
          </p>
          <div className="flex items-center gap-3 max-w-xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search services..." className="pl-10 h-12 text-base" data-testid="service-search" />
            </div>
            <Button size="lg" onClick={() => navigate('/plan')} className="h-12 bg-indigo-600 hover:bg-indigo-700" data-testid="plan-event-cta">
              Plan Your Event <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        {/* City Pills */}
        <div className="flex gap-2 flex-wrap mb-4 justify-center" data-testid="city-filters">
          {CITIES.map(c => (
            <Button key={c.key} variant={selectedCity === c.key ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCity(c.key)} className="gap-1" data-testid={`city-${c.key}`}>
              {c.key !== 'all' && <MapPin className="h-3 w-3" />}
              {c.label}
              {c.key !== 'all' && <span className="text-[10px] opacity-70">({cityCount(c.key)})</span>}
            </Button>
          ))}
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 flex-wrap mb-8 justify-center" data-testid="cat-filters">
          {CATEGORIES.map(c => (
            <Button key={c.key} variant={selectedCategory === c.key ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCategory(c.key)} className="text-xs">
              {c.label}
            </Button>
          ))}
        </div>

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-6">{filtered.length} services found</p>

        {/* Services Grid */}
        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Loading services...</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="services-grid">
            {filtered.map(s => {
              const cityLabel = CITIES.find(c => c.key === s.city)?.label || s.city;
              return (
                <Card key={s.service_id || s.product_id} className="border-border hover:border-indigo-500/30 transition-all group cursor-pointer overflow-hidden" onClick={() => navigate('/plan')} data-testid={`service-${s.service_id}`}>
                  {s.image_url && (
                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                      <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-sm leading-tight">{s.name}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{s.short_description || s.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-indigo-500">
                        ${customerUnitPrice(s).toFixed(2)}{s.pricing_model === 'per_person' ? '/pp' : s.pricing_model === 'flat_rate' ? ' flat' : s.pricing_model === 'per_hour' ? '/hr' : '/pp'}
                      </span>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-[9px] px-1.5">{cityLabel.split(',')[0]}</Badge>
                        <Badge variant="outline" className="text-[9px] px-1.5 capitalize">{s.category}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* CTA Section */}
        <section className="mt-16 text-center py-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white px-6">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to Plan Your Event?</h2>
          <p className="text-indigo-100 mb-6 max-w-lg mx-auto">Get an instant quote with our event planning tool. Select services, customize your order, and we handle the rest.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50" onClick={() => navigate('/plan')}>
              Plan Your Event <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => navigate('/events')}>
              Browse Events
            </Button>
          </div>
        </section>

        {/* City Cards for SEO */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold mb-6 text-center">Event Services by City</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CITIES.filter(c => c.key !== 'all').map(city => (
              <Card key={city.key} className="border-border hover:border-indigo-500/30 transition-colors cursor-pointer group" onClick={() => navigate(`/${city.slug}`)}>
                <CardContent className="p-5 text-center">
                  <MapPin className="h-8 w-8 text-indigo-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold mb-1">{city.label}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{city.tagline}</p>
                  <p className="text-sm font-semibold text-indigo-500">{cityCount(city.key)} Services</p>
                  <Button variant="ghost" size="sm" className="mt-2 text-xs">
                    Explore <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
